import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import RenameIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import type { ChangeEventHandler, KeyboardEventHandler } from "react";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import FileIcon from "@entrilia/shared/components/FileIcon";
import TypographyMultilineEllipsis from "@entrilia/shared/components/TypographyMultilineEllipsis";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { combineValidators, fileNameValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import type { FileDetails, FileInfo } from "../../../../api/types/fileTypes";
import { useFilesContext } from "../FilesContext";
import { renameFileInGrid } from "../filesState";
import MalwareScanBadge from "./MalwareScanBadge";

interface Props {
  fileDetails: FileDetails;
  onFileInfoUpdated: (newFileInfo: FileInfo) => void;
}

const renameFile = withErrorHandling(adminApi.renameFile);
const renameEntityFile = withErrorHandling(adminApi.renameEntityFile);

const validateFileName = combineValidators(requiredValidator, fileNameValidator());

interface RenamingState {
  status: "none" | "edit" | "submit";
  fileNameWithoutExt: string;
  isFileNameValid: boolean;
  validationError?: string;
}

const getInitialRenamingState = (initialFileName: string): RenamingState => ({
  status: "none",
  fileNameWithoutExt: initialFileName.substring(0, initialFileName.length - getFileExtension(initialFileName).length),
  isFileNameValid: true,
});

const FileDetailsHeaderSection = ({ fileDetails, onFileInfoUpdated }: Props) => {
  const { onDownloadFile, onDeleteFile, hasPermissionsToManage, gridApiRef, entityInfo } = useFilesContext();
  const { sendNotificationError } = useNotificationContext();
  const [renamingState, setRenamingState] = useState<RenamingState>(() =>
    getInitialRenamingState(fileDetails.fileName)
  );

  const fileExtension = getFileExtension(fileDetails.fileName);

  const onStopRenaming = () => {
    setRenamingState(getInitialRenamingState(fileDetails.fileName));
  };

  const handleFileNameInputKeyDown: KeyboardEventHandler = (e) => {
    e.stopPropagation();

    if (e.code === "Escape") {
      e.preventDefault();
      onStopRenaming();
    }
  };

  const handleFileNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const fileNameWithoutExt = e.target.value;
    const { isValid, error } = validateFileName(fileNameWithoutExt);

    setRenamingState((state) => ({
      ...state,
      fileNameWithoutExt,
      isFileNameValid: isValid,
      validationError: error,
    }));
  };

  const handleApplyRenaming = async () => {
    const newFileName = renamingState.fileNameWithoutExt + fileExtension;
    const { isValid, error } = validateFileName(newFileName);
    if (!isValid) {
      setRenamingState((state) => ({ ...state, isFileNameValid: false, validationError: error }));
      return;
    }

    setRenamingState((state) => ({ ...state, status: "submit" }));

    const [newFileInfo, respError] = entityInfo
      ? await renameEntityFile(
          entityInfo.entityType,
          entityInfo.entityId,
          entityInfo.objectType,
          fileDetails.catalogueId,
          newFileName
        )
      : await renameFile(fileDetails.catalogueId, newFileName);

    if (respError) {
      sendNotificationError(respError.message);
      onStopRenaming();
      return;
    }

    renameFileInGrid(fileDetails.catalogueId, newFileName, gridApiRef);
    onFileInfoUpdated(newFileInfo);
    onStopRenaming();
  };

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      {renamingState.status !== "none" && (
        <Stack spacing={1} sx={{ pb: 1, width: "100%" }}>
          <TextField
            variant="outlined"
            fullWidth
            value={renamingState.fileNameWithoutExt}
            onChange={handleFileNameChange}
            onKeyDown={handleFileNameInputKeyDown}
            error={!renamingState.isFileNameValid}
            helperText={renamingState.validationError}
            slotProps={{
              input: {
                endAdornment: <Typography color="textSecondary">{fileExtension}</Typography>,
              },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="text" color="secondary" onClick={onStopRenaming}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!renamingState.isFileNameValid}
              loading={renamingState.status === "submit"}
              onClick={handleApplyRenaming}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      )}
      {renamingState.status === "none" && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ pb: 1, justifyContent: "space-between", alignItems: "center", width: "100%" }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "90%" }}>
            <FileIcon fileExtension={fileExtension} />
            <TypographyMultilineEllipsis
              maxLines={3}
              typographyProps={{ variant: "subtitle1", sx: { lineBreak: "anywhere" } }}
            >
              {fileDetails.fileName}
            </TypographyMultilineEllipsis>
          </Stack>
          <ActionsMenuButton
            items={[
              {
                disabled: !hasPermissionsToManage,
                label: "Rename",
                icon: <RenameIcon />,
                onClick: () => setRenamingState((state) => ({ ...state, status: "edit" })),
              },
              {
                disabled: !hasPermissionsToManage,
                label: "Delete",
                icon: <DeleteIcon color="error" />,
                onClick: () => onDeleteFile(fileDetails),
              },
            ]}
          />
        </Stack>
      )}
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        startIcon={<DownloadIcon />}
        onClick={() => onDownloadFile(fileDetails)}
      >
        Download
      </Button>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <MalwareScanBadge scanResult={fileDetails.malwareScanResult} />
      </Box>
    </Stack>
  );
};

export default FileDetailsHeaderSection;
